import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Image, MessageSquare, Settings, LogOut,
  Plus, Trash2, Edit2, Check, X, Eye, Clock,
  PoundSterling, MapPin, ChevronDown, Search, Filter
} from 'lucide-react';
import {
  verifyAdmin, adminLogout, getBookings, updateBooking, deleteBooking,
  getPortfolio, createPortfolioItem, deletePortfolioItem,
  getTestimonials, createTestimonial, deleteTestimonial,
  getSettings, updateSettings
} from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';

// Status badge colors
const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  completed: 'bg-accent-purple/20 text-accent-purple border-accent-purple/30',
};

// Bookings Tab
const BookingsTab = ({ bookings, onUpdate, onDelete }) => {
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredBookings = bookings.filter(b => {
    const matchesFilter = filter === 'all' || b.status === filter;
    const matchesSearch = b.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleStatusChange = async (id, status) => {
    try {
      await onUpdate(id, { status });
      toast.success(`Booking ${status}`);
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-dark-surface border border-white/10 rounded-lg py-2 pl-10 pr-4 text-white placeholder:text-slate-500 focus:border-accent-purple outline-none"
            data-testid="bookings-search"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'rejected', 'completed'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm capitalize transition-colors ${filter === status
                ? 'bg-accent-purple text-white'
                : 'bg-dark-surface text-slate-400 hover:text-white'
                }`}
              data-testid={`filter-${status}`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Bookings List */}
      <div className="space-y-4">
        {filteredBookings.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            No bookings found
          </div>
        ) : (
          filteredBookings.map((booking) => (
            <motion.div
              key={booking.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-xl p-6 cursor-pointer hover:border-accent-purple/30 transition-colors"
              onClick={() => setSelectedBooking(booking)}
              data-testid={`booking-card-${booking.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-medium text-lg">{booking.full_name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full border ${statusColors[booking.status]}`}>
                      {booking.status}
                    </span>
                  </div>
                  <p className="text-slate-400 text-sm">{booking.email}</p>
                </div>
                <div className="flex items-center gap-6 text-sm text-slate-400">
                  <div className="flex items-center gap-2">
                    <MapPin size={14} />
                    {booking.tattoo_placement}
                  </div>
                  <div className="flex items-center gap-2">
                    <PoundSterling size={14} />
                    {booking.budget_range}
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={14} />
                    {new Date(booking.created_at).toLocaleDateString()}
                  </div>
                  <div className={`text-xs font-bold uppercase px-2 py-1 rounded ${booking.payment_status === 'paid' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'
                    }`}>
                    {booking.payment_status || 'unpaid'}
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Booking Detail Modal */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="bg-dark-surface border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedBooking && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <DialogTitle className="font-heading text-2xl">{selectedBooking.full_name}</DialogTitle>
                  <span className={`text-xs px-2 py-1 rounded-full border ${statusColors[selectedBooking.status]}`}>
                    {selectedBooking.status}
                  </span>
                </div>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-6 my-6">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Email</p>
                  <p>{selectedBooking.email}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Phone</p>
                  <p>{selectedBooking.phone}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Placement</p>
                  <p>{selectedBooking.tattoo_placement}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Budget (£)</p>
                  <p>{selectedBooking.budget_range}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Preferred Date</p>
                  <p>{selectedBooking.preferred_date || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Payment Status</p>
                  <p className={`capitalize ${selectedBooking.payment_status === 'paid' ? 'text-green-500 font-bold' : 'text-yellow-500'}`}>
                    {selectedBooking.payment_status || 'unpaid'}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Description</p>
                <p className="text-slate-300 bg-dark rounded-lg p-4">{selectedBooking.description}</p>
              </div>

              <div className="mb-6">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Submitted</p>
                <p>{new Date(selectedBooking.created_at).toLocaleString()}</p>
              </div>

              <DialogFooter className="flex gap-2">
                {selectedBooking.status === 'pending' && (
                  <>
                    <button
                      onClick={() => {
                        handleStatusChange(selectedBooking.id, 'approved');
                        setSelectedBooking(null);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                      data-testid="approve-booking-btn"
                    >
                      <Check size={16} /> Approve
                    </button>
                    <button
                      onClick={() => {
                        handleStatusChange(selectedBooking.id, 'rejected');
                        setSelectedBooking(null);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                      data-testid="reject-booking-btn"
                    >
                      <X size={16} /> Reject
                    </button>
                  </>
                )}
                {selectedBooking.status === 'approved' && (
                  <button
                    onClick={() => {
                      handleStatusChange(selectedBooking.id, 'completed');
                      setSelectedBooking(null);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-accent-purple/20 text-accent-purple rounded-lg hover:bg-accent-purple/30 transition-colors"
                    data-testid="complete-booking-btn"
                  >
                    <Check size={16} /> Mark Complete
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to delete this booking?')) {
                      await onDelete(selectedBooking.id);
                      setSelectedBooking(null);
                      toast.success('Booking deleted');
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                  data-testid="delete-booking-btn"
                >
                  <Trash2 size={16} /> Delete
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Portfolio Tab
const PortfolioTab = ({ portfolio, onCreate, onDelete }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    style: '',
    placement: '',
    session_time: '',
    image_url: '',
    featured: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onCreate(newItem);
      setShowAddForm(false);
      setNewItem({
        title: '',
        description: '',
        style: '',
        placement: '',
        session_time: '',
        image_url: '',
        featured: false
      });
      toast.success('Portfolio item added');
    } catch (error) {
      toast.error('Failed to add portfolio item');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl">Portfolio Items ({portfolio.length})</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-accent-purple text-white rounded-lg hover:bg-accent-purple/80 transition-colors"
          data-testid="add-portfolio-btn"
        >
          <Plus size={16} /> Add Item
        </button>
      </div>

      {/* Portfolio Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {portfolio.map((item) => (
          <motion.div
            key={item.id}
            layout
            className="glass rounded-xl overflow-hidden group"
            data-testid={`portfolio-admin-${item.id}`}
          >
            <div className="aspect-square overflow-hidden">
              <img
                src={item.image_url}
                alt={item.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-medium">{item.title}</h3>
                  <p className="text-sm text-slate-400">{item.style}</p>
                </div>
                {item.featured && (
                  <Badge className="bg-accent-purple/20 text-accent-purple border-0">Featured</Badge>
                )}
              </div>
              <button
                onClick={async () => {
                  if (window.confirm('Delete this portfolio item?')) {
                    await onDelete(item.id);
                    toast.success('Item deleted');
                  }
                }}
                className="text-red-400 text-sm hover:text-red-300 transition-colors"
                data-testid={`delete-portfolio-${item.id}`}
              >
                Delete
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add Form Modal */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="bg-dark-surface border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Portfolio Item</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Title</label>
              <input
                type="text"
                required
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                className="w-full bg-dark border border-white/10 rounded-lg p-3 text-white focus:border-accent-purple outline-none"
                data-testid="portfolio-title-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Image URL</label>
              <input
                type="url"
                required
                value={newItem.image_url}
                onChange={(e) => setNewItem({ ...newItem, image_url: e.target.value })}
                className="w-full bg-dark border border-white/10 rounded-lg p-3 text-white focus:border-accent-purple outline-none"
                placeholder="https://..."
                data-testid="portfolio-image-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Style</label>
                <input
                  type="text"
                  required
                  value={newItem.style}
                  onChange={(e) => setNewItem({ ...newItem, style: e.target.value })}
                  className="w-full bg-dark border border-white/10 rounded-lg p-3 text-white focus:border-accent-purple outline-none"
                  data-testid="portfolio-style-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Placement</label>
                <input
                  type="text"
                  required
                  value={newItem.placement}
                  onChange={(e) => setNewItem({ ...newItem, placement: e.target.value })}
                  className="w-full bg-dark border border-white/10 rounded-lg p-3 text-white focus:border-accent-purple outline-none"
                  data-testid="portfolio-placement-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Session Time</label>
              <input
                type="text"
                required
                value={newItem.session_time}
                onChange={(e) => setNewItem({ ...newItem, session_time: e.target.value })}
                className="w-full bg-dark border border-white/10 rounded-lg p-3 text-white focus:border-accent-purple outline-none"
                placeholder="e.g., 6 hours"
                data-testid="portfolio-time-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Description</label>
              <textarea
                required
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                rows={3}
                className="w-full bg-dark border border-white/10 rounded-lg p-3 text-white focus:border-accent-purple outline-none resize-none"
                data-testid="portfolio-desc-input"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={newItem.featured}
                onChange={(e) => setNewItem({ ...newItem, featured: e.target.checked })}
                data-testid="portfolio-featured-checkbox"
              />
              <span className="text-sm text-slate-400">Mark as featured</span>
            </label>
            <DialogFooter>
              <button
                type="submit"
                className="px-6 py-2 bg-accent-purple text-white rounded-lg hover:bg-accent-purple/80 transition-colors"
                data-testid="portfolio-submit-btn"
              >
                Add Item
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Testimonials Tab
const TestimonialsTab = ({ testimonials, onCreate, onDelete }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({
    client_name: '',
    content: '',
    rating: 5
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onCreate(newItem);
      setShowAddForm(false);
      setNewItem({ client_name: '', content: '', rating: 5 });
      toast.success('Testimonial added');
    } catch (error) {
      toast.error('Failed to add testimonial');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl">Testimonials ({testimonials.length})</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-accent-purple text-white rounded-lg hover:bg-accent-purple/80 transition-colors"
          data-testid="add-testimonial-btn"
        >
          <Plus size={16} /> Add Testimonial
        </button>
      </div>

      {/* Testimonials List */}
      <div className="space-y-4">
        {testimonials.map((item) => (
          <motion.div
            key={item.id}
            layout
            className="glass rounded-xl p-6"
            data-testid={`testimonial-admin-${item.id}`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-medium">{item.client_name}</h3>
                <div className="flex gap-1 mt-1">
                  {[...Array(item.rating)].map((_, i) => (
                    <span key={i} className="text-accent-purple">★</span>
                  ))}
                </div>
              </div>
              <button
                onClick={async () => {
                  if (window.confirm('Delete this testimonial?')) {
                    await onDelete(item.id);
                    toast.success('Testimonial deleted');
                  }
                }}
                className="text-red-400 hover:text-red-300 transition-colors"
                data-testid={`delete-testimonial-${item.id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
            <p className="text-slate-400">"{item.content}"</p>
          </motion.div>
        ))}
      </div>

      {/* Add Form Modal */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="bg-dark-surface border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Testimonial</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Client Name</label>
              <input
                type="text"
                required
                value={newItem.client_name}
                onChange={(e) => setNewItem({ ...newItem, client_name: e.target.value })}
                className="w-full bg-dark border border-white/10 rounded-lg p-3 text-white focus:border-accent-purple outline-none"
                data-testid="testimonial-name-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Content</label>
              <textarea
                required
                value={newItem.content}
                onChange={(e) => setNewItem({ ...newItem, content: e.target.value })}
                rows={4}
                className="w-full bg-dark border border-white/10 rounded-lg p-3 text-white focus:border-accent-purple outline-none resize-none"
                data-testid="testimonial-content-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Rating</label>
              <select
                value={newItem.rating}
                onChange={(e) => setNewItem({ ...newItem, rating: parseInt(e.target.value) })}
                className="w-full bg-dark border border-white/10 rounded-lg p-3 text-white focus:border-accent-purple outline-none"
                data-testid="testimonial-rating-select"
              >
                {[5, 4, 3, 2, 1].map((r) => (
                  <option key={r} value={r}>{r} Stars</option>
                ))}
              </select>
            </div>
            <DialogFooter>
              <button
                type="submit"
                className="px-6 py-2 bg-accent-purple text-white rounded-lg hover:bg-accent-purple/80 transition-colors"
                data-testid="testimonial-submit-btn"
              >
                Add Testimonial
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Settings Tab
const SettingsTab = ({ settings, onUpdate }) => {
  const [localSettings, setLocalSettings] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(localSettings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl">
      <h2 className="font-heading text-xl mb-6">Site Settings</h2>

      <div className="glass rounded-xl p-6 space-y-6">
        <div>
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              className={`w-12 h-6 rounded-full relative transition-colors ${localSettings?.books_open ? 'bg-green-500' : 'bg-red-500'
                }`}
              onClick={() => setLocalSettings({ ...localSettings, books_open: !localSettings?.books_open })}
            >
              <div
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${localSettings?.books_open ? 'translate-x-7' : 'translate-x-1'
                  }`}
              />
            </div>
            <div>
              <p className="font-medium">Books {localSettings?.books_open ? 'Open' : 'Closed'}</p>
              <p className="text-sm text-slate-500">Toggle to accept or pause new bookings</p>
            </div>
          </label>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Availability Message</label>
          <input
            type="text"
            value={localSettings?.availability_message || ''}
            onChange={(e) => setLocalSettings({ ...localSettings, availability_message: e.target.value })}
            className="w-full bg-dark border border-white/10 rounded-lg p-3 text-white focus:border-accent-purple outline-none"
            placeholder="e.g., Currently accepting new clients"
            data-testid="settings-message-input"
          />
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-accent-purple text-white rounded-lg hover:bg-accent-purple/80 transition-colors disabled:opacity-50"
          data-testid="settings-save-btn"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

// Main Dashboard Component
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        await verifyAdmin();
        await loadData();
      } catch (error) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    };
    init();
  }, [navigate]);

  const loadData = async () => {
    try {
      const [bookingsRes, portfolioRes, testimonialsRes, settingsRes] = await Promise.all([
        getBookings(),
        getPortfolio(),
        getTestimonials(),
        getSettings()
      ]);
      setBookings(bookingsRes.data);
      setPortfolio(portfolioRes.data);
      setTestimonials(testimonialsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
    } catch (error) { }
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  // CRUD handlers
  const handleUpdateBooking = async (id, data) => {
    await updateBooking(id, data);
    await loadData();
  };

  const handleDeleteBooking = async (id) => {
    await deleteBooking(id);
    await loadData();
  };

  const handleCreatePortfolio = async (data) => {
    await createPortfolioItem(data);
    await loadData();
  };

  const handleDeletePortfolio = async (id) => {
    await deletePortfolioItem(id);
    await loadData();
  };

  const handleCreateTestimonial = async (data) => {
    await createTestimonial(data);
    await loadData();
  };

  const handleDeleteTestimonial = async (id) => {
    await deleteTestimonial(id);
    await loadData();
  };

  const handleUpdateSettings = async (data) => {
    await updateSettings(data);
    await loadData();
  };

  if (loading) {
    return (
      <div className="h-screen bg-dark flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-2 border-accent-purple border-t-transparent rounded-full"
        />
      </div>
    );
  }

  const pendingBookings = bookings.filter(b => b.status === 'pending').length;

  return (
    <div className="min-h-screen bg-dark" data-testid="admin-dashboard">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="font-heading text-xl" data-testid="dashboard-logo">
              TATTOOS BY HET
            </a>
            <span className="text-xs uppercase tracking-widest text-slate-500">Admin</span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass rounded-xl p-6">
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Total Bookings</p>
            <p className="font-heading text-3xl">{bookings.length}</p>
          </div>
          <div className="glass rounded-xl p-6">
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Pending</p>
            <p className="font-heading text-3xl text-yellow-400">{pendingBookings}</p>
          </div>
          <div className="glass rounded-xl p-6">
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Portfolio Items</p>
            <p className="font-heading text-3xl">{portfolio.length}</p>
          </div>
          <div className="glass rounded-xl p-6">
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Testimonials</p>
            <p className="font-heading text-3xl">{testimonials.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="glass mb-6 p-1 rounded-xl">
            <TabsTrigger value="bookings" className="data-[state=active]:bg-accent-purple rounded-lg" data-testid="tab-bookings">
              <Calendar size={16} className="mr-2" />
              Bookings
              {pendingBookings > 0 && (
                <span className="ml-2 bg-yellow-500 text-black text-xs px-2 py-0.5 rounded-full">
                  {pendingBookings}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="data-[state=active]:bg-accent-purple rounded-lg" data-testid="tab-portfolio">
              <Image size={16} className="mr-2" />
              Portfolio
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="data-[state=active]:bg-accent-purple rounded-lg" data-testid="tab-testimonials">
              <MessageSquare size={16} className="mr-2" />
              Testimonials
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-accent-purple rounded-lg" data-testid="tab-settings">
              <Settings size={16} className="mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <BookingsTab
              bookings={bookings}
              onUpdate={handleUpdateBooking}
              onDelete={handleDeleteBooking}
            />
          </TabsContent>

          <TabsContent value="portfolio">
            <PortfolioTab
              portfolio={portfolio}
              onCreate={handleCreatePortfolio}
              onDelete={handleDeletePortfolio}
            />
          </TabsContent>

          <TabsContent value="testimonials">
            <TestimonialsTab
              testimonials={testimonials}
              onCreate={handleCreateTestimonial}
              onDelete={handleDeleteTestimonial}
            />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab
              settings={settings}
              onUpdate={handleUpdateSettings}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
